/**
 * Route log writes — the pure planning behind the dot / day / count deletes of
 * the route modal (docs/spec/11-map-routes.md, "What each tap writes" and
 * "Fixing and deleting — the dots, the days, the Napló").
 *
 * `useMainApp` owns the Firestore calls; this module decides WHICH entries
 * go, WHICH numbers change and what `totals` / `routeIds` become, so each rule
 * can be tested without Firebase (the counterpart of `sprayLogWrites.js`).
 *
 * `dayDocs` is the shape the per-route query yields, flattened by the hook:
 * `[{ date: "YYYY-MM-DD", climbs: { [cid]: entry } }]` — the climber's own day
 * documents that hold an entry of the route, in any order. Only the route half
 * of a day document is ever planned; the plans never name a spray field.
 *
 * The plans return plain data (cid lists, numbers, `totals` objects). The hook
 * turns `deleteCids` into `deleteField()` and `dropRouteId` into `arrayRemove`.
 */
import { CLIMB_KIND, attemptEntryId, computeClimbTotals } from "@sendly/shared";
import { clampAttempts } from "./routeLogModel";

const isObj = (v) => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const count = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Math.floor(Number(v)) : 0);
const entriesOfRoute = (climbs, rid) =>
  Object.entries(isObj(climbs) ? climbs : {}).filter(([, e]) => e && e.routeId === rid);

/** The route's send entry across every day: `{ date, cid, entry }`, or null. */
export function findSendEntry(dayDocs, rid) {
  for (const d of dayDocs || []) {
    for (const [cid, entry] of entriesOfRoute(d?.climbs, rid)) {
      if (entry.kind === CLIMB_KIND.SEND) return { date: d.date, cid, entry };
    }
  }
  return null;
}

/**
 * The tries logged on days BEFORE `sendDate` (the route's `attempt_{rid}`
 * entries). They are real try dots that the send's own count builds on: the send
 * entry stores `ascent.attempts − earlierTries`, so the day timeline of the
 * send's day reads only that day's tries.
 */
export function earlierTriesBefore(dayDocs, rid, sendDate) {
  if (!sendDate) return 0;
  const key = attemptEntryId(rid);
  let sum = 0;
  for (const d of dayDocs || []) {
    if (!d || !(d.date < sendDate)) continue;
    const entry = isObj(d.climbs) ? d.climbs[key] : null;
    if (entry && entry.routeId === rid && entry.kind === CLIMB_KIND.ATTEMPT) sum += count(entry.attempts);
  }
  return sum;
}

/**
 * "Hányadik próbára sikerült?" / a virtual try dot deleted: the ascent's total
 * becomes `n` (clamped 1–999) and the send entry's `attempts` becomes
 * `n − earlierTries` (never below 1). Nothing else moves — no day's try entry.
 *
 * Returns `{ ascentAttempts, sendEntry }` where `sendEntry` is
 * `{ date, cid, attempts }`, or null when the send entry is not found (then only
 * the ascent changes). Returns null for an `n` that is not a count.
 */
export function planSetSendAttempts({ dayDocs, rid, n }) {
  const ascentAttempts = clampAttempts(n);
  if (ascentAttempts == null) return null;
  const send = findSendEntry(dayDocs, rid);
  if (!send) return { ascentAttempts, sendEntry: null };
  const earlier = earlierTriesBefore(dayDocs, rid, send.date);
  return {
    ascentAttempts,
    sendEntry: { date: send.date, cid: send.cid, attempts: Math.max(1, ascentAttempts - earlier) },
  };
}

/**
 * Take `by` tries off one day's `attempt_{rid}` entry (`by: null` drops the
 * entry — "delete the project day"); reaching 0 drops it too. `climbs` is that
 * ONE day's map.
 *
 * On the send's day the send entry's `attempts` drops by the removed count as
 * well (never below 1), so the day timeline keeps up with the dots. A send entry
 * with no numeric `attempts` (an unknown count) is left alone.
 *
 * Returns null when the day has no such entry, otherwise
 * `{ key, remaining, removed, removeEntry, totals, dropRouteId, sendEntry }`
 * where `remaining` is the entry's new count (0 when it goes), `removed` the
 * tries taken, `dropRouteId` true when no entry of the route is left that day,
 * and `sendEntry` is `{ cid, attempts }` or null.
 */
export function planAttemptReduction({ climbs, rid, by = null }) {
  const key = attemptEntryId(rid);
  const entry = isObj(climbs) ? climbs[key] : null;
  if (!entry) return null;
  const current = Number(entry.attempts) || 0;
  const take = by == null ? null : count(by);
  const remaining = take == null ? 0 : Math.max(0, current - take);
  const removed = remaining > 0 ? current - remaining : current;
  const removeEntry = remaining <= 0;

  const next = { ...climbs };
  if (removeEntry) delete next[key];
  else next[key] = { ...entry, attempts: remaining };

  let sendEntry = null;
  for (const [cid, e] of entriesOfRoute(climbs, rid)) {
    if (e.kind !== CLIMB_KIND.SEND || typeof e.attempts !== "number") continue;
    const lowered = Math.max(1, e.attempts - removed);
    if (removed > 0 && lowered !== e.attempts) {
      sendEntry = { cid, attempts: lowered };
      next[cid] = { ...e, attempts: lowered };
    }
    break;
  }

  return {
    key,
    remaining: removeEntry ? 0 : remaining,
    removed,
    removeEntry,
    totals: computeClimbTotals(next),
    dropRouteId: removeEntry && entriesOfRoute(next, rid).length === 0,
    sendEntry,
  };
}

/**
 * Delete one day of a route's log — ONE batch (the selection bar's "Az egész
 * nap").
 *
 * - The day holds the send: the ascent goes, the send entry and EVERY repeat
 *   entry on every day go, and that day's `attempt` entry goes. The other days'
 *   `attempt` entries stay (the route becomes a project again).
 * - Any other day: that day's `attempt` entry and that day's repeat entries go;
 *   an ascent with a numeric `attempts` is lowered by the deleted tries (never
 *   below 1).
 *
 * Returns null when nothing would change. Otherwise
 * `{ sendDay, deleteAscent, ascentAttempts, dayOps }`: `ascentAttempts` is the
 * ascent's new total (null = unchanged), `dayOps` one
 * `{ date, deleteCids, totals, dropRouteId }` per day document that loses an
 * entry — `totals` from the surviving map, `dropRouteId` when no entry of the
 * route is left on that day.
 */
export function planRemoveRouteDay({ dayDocs, rid, date, hasAscent = false, ascentAttempts = null }) {
  if (!rid || !date) return null;
  const sendDay = (dayDocs || []).some(d => d && d.date === date
    && entriesOfRoute(d.climbs, rid).some(([, e]) => e.kind === CLIMB_KIND.SEND));

  const dayOps = [];
  let removedTries = 0;
  for (const d of dayDocs || []) {
    if (!d) continue;
    const kept = { ...(isObj(d.climbs) ? d.climbs : {}) };
    const deleteCids = [];
    for (const [cid, e] of entriesOfRoute(d.climbs, rid)) {
      const onDate = d.date === date;
      const goes = sendDay
        ? e.kind === CLIMB_KIND.SEND || e.kind === CLIMB_KIND.REPEAT || (onDate && e.kind === CLIMB_KIND.ATTEMPT)
        : onDate && (e.kind === CLIMB_KIND.ATTEMPT || e.kind === CLIMB_KIND.REPEAT);
      if (!goes) continue;
      deleteCids.push(cid);
      delete kept[cid];
      if (onDate && e.kind === CLIMB_KIND.ATTEMPT) removedTries += count(e.attempts);
    }
    if (!deleteCids.length) continue;
    dayOps.push({
      date: d.date,
      deleteCids,
      totals: computeClimbTotals(kept),
      dropRouteId: entriesOfRoute(kept, rid).length === 0,
    });
  }

  const deleteAscent = sendDay && !!hasAscent;
  let nextAscentAttempts = null;
  if (!sendDay && hasAscent && typeof ascentAttempts === "number" && removedTries > 0) {
    const lowered = Math.max(1, ascentAttempts - removedTries);
    if (lowered !== ascentAttempts) nextAscentAttempts = lowered;
  }
  if (!dayOps.length && !deleteAscent && nextAscentAttempts == null) return null;
  return { sendDay, deleteAscent, ascentAttempts: nextAscentAttempts, dayOps };
}

/**
 * Today's map as the flash correction (D13) must see it right after the send
 * and the repeats were deleted: the route's `send` / `repeat` entries are gone,
 * its `attempt` entry (there is none in this case) and every other route stay.
 * The local listener has not necessarily caught up yet, so the create that
 * follows computes its `totals` from this instead of from the stale map.
 */
export function mapWithoutRouteSends(climbs, rid) {
  const out = {};
  for (const [cid, e] of Object.entries(isObj(climbs) ? climbs : {})) {
    if (e && e.routeId === rid && (e.kind === CLIMB_KIND.SEND || e.kind === CLIMB_KIND.REPEAT)) continue;
    out[cid] = e;
  }
  return out;
}
